import { ExportToCsv } from 'export-to-csv';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { WithDbId } from '../../../../adl-gen/common/db';
import { AppClient, UserUploadRecord } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { UserUploadData } from '../../../../adl-gen/totalinteraction/futurefit/requests';
import { Loader, MaybeLoading } from '../../../../loader/loader';
import { CloseIcon, PlusIcon } from '../../../../ui/common/icons';
import { Button } from '../../../../ui/widgets/button/button';
import { Table, TableCell, TableRow } from '../../../../ui/widgets/table/table';
import { AppUserUploadStore, CSVField } from '../../../stores';
import { CSVDropzoneObserver } from '../../common/editor';
import { AdminPageLayoutFn } from '../../common/page-layout/admin-page-layout';
import { FileContent } from '../../components/dropzone/dropzone';

const styles = require('../../common/details-view.css');
const fonts = require('../../../../ui/common/fonts.css');

interface AppUserUploadScreenProps {
  pageLayout: AdminPageLayoutFn;

  store: AppUserUploadStore;

  client: MaybeLoading<WithDbId<AppClient>>;

  onCancel?(): Promise<void>;
  onUpload?(data: UserUploadData): Promise<void>;
}

@observer
export class AppUserUploadScreen extends React.Component<AppUserUploadScreenProps> {
  @observable fileData = {
    files: Array<File>(),
    contents: Array<FileContent>()
  };

  @observable data?: UserUploadData;

  @action getFiles = (files: Array<File>) => {
    this.fileData.files = files;
  };

  @action getContent = (contents: Array<FileContent>) => {
    this.fileData.contents = contents;
  };

  get uploadFields() {
    return this.props.store.fields;
  }

  @action getUploadDataFromContent = (content: FileContent) => {
    this.data = this.props.store.getAppUserUpload(content);
    return this.data;
  };

  getCsvExporter = (): ExportToCsv => {
    const options = {
      filename: 'upload-template',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      title: undefined,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
      // headers: this.uploadFields.map(f => f.name)
    };

    return new ExportToCsv(options);
  };

  generateEmptyCsv = (exporter: ExportToCsv): void => {
    const { userUploadFields } = this.props.store;
    const emptyItem = {
      [userUploadFields.LASTNAME]: '',
      [userUploadFields.FIRSTNAME]: '',
      [userUploadFields.EMAIL]: '',
      [userUploadFields.MANAGER_NAME]: '',
      [userUploadFields.MANAGER_EMAIL]: '',
      [userUploadFields.ASSESSMENT_1]: '',
      [userUploadFields.ASSESSMENT_2]: '',
      [userUploadFields.ASSESSMENT_3]: '',
      [userUploadFields.USERGROUP_1]: '',
      [userUploadFields.USERGROUP_2]: '',
      [userUploadFields.USERGROUP_3]: '',
      [userUploadFields.USERGROUP_4]: '',
      [userUploadFields.USERGROUP_5]: ''
    };
    exporter.generateCsv([emptyItem]);
  };

  onCancel = async () => {
    if (this.props.onCancel) {
      await this.props.onCancel();
    }
  };

  onUpload = async () => {
    if (this.props.onUpload && this.data) {
      // TODO: get result etc - maybe replace this with a nav
      await this.props.onUpload(this.data);
    }
  };

  onDownloadTemplate = () => {
    const csvExporter = this.getCsvExporter();
    // this.generateEmptyCsv(csvExporter);
    this.generateEmptyCsv(csvExporter);
  };

  /// TODO Change 'comment' to 'help', append 'HelpIcon', provide onHover modal with more detail
  FileDropObserver = observer(() =>
    CSVDropzoneObserver({
      files: this.getFiles,
      contents: this.getContent,
      multiple: false,
      title: 'Upload users',
      comment: `CSV Format (header row): \n${this.uploadFields.map(f => f.name).join(', ')}`,
      xsSpan: 4
    })
  );

  renderTopButtons = () => {
    const processButton = () => {
      return (
        <div className={styles.buttonList}>
          <Button
            className={styles.button}
            variant="round"
            onClick={async () => this.onUpload()}
            Icon={PlusIcon}
            size="small"
            disabled={this.fileData.contents.length === 0}
          >
            Process Upload
          </Button>
        </div>
      );
    };

    const downloadTemplateButton = () => {
      return (
        <div className={styles.buttonList}>
          <Button
            className={styles.button}
            onClick={async () => this.onDownloadTemplate()}
            variant="round"
            // Icon={TODO}
            size="small"
            disabled={false}
          >
            Download Template CSV
          </Button>
        </div>
      );
    };

    const cancelButton = () => {
      return (
        <div className={styles.buttonList}>
          <Button
            className={styles.button}
            onClick={async () => this.onCancel()}
            variant="round-secondary"
            Icon={CloseIcon}
            size="small"
            disabled={false}
          >
            Cancel
          </Button>
        </div>
      );
    };

    /// TODO
    // const downloadCurrentUsersAndGroupsButton = () => {
    //   return (
    //     <div className={styles.buttonList}>
    //       <Button
    //         className={styles.button}
    //         onClick={async () => this.onDownloadCurrentUsersAndGroupsSet()}
    //         // Icon={TODO}
    //         size="small"
    //         disabled={false}
    //       >
    //         Download Existing Users and Groups
    //       </Button>
    //     </div>
    //   )
    // }

    return (
      <React.Fragment>
        {cancelButton()}
        {processButton()}
        {downloadTemplateButton()}
        {/* {downloadCurrentUsersAndGroupsButton()} */}
      </React.Fragment>
    );
  };

  // TODO: Make this a ListView
  renderFileContent = (input: UserUploadData) => (
    <Table>
      <TableRow header>
        {Object.values(this.uploadFields).map((field, idx) => {
          return (
            <TableCell key={idx}>
              <div>{field.label}</div>
            </TableCell>
          );
        })}
      </TableRow>
      {input.list.map((row, idx) => {
        return <TableRow key={idx}>{...this.renderRow(this.uploadFields, row)}</TableRow>;
      })}
    </Table>
  );

  renderRow = (colInfo: CSVField[], row: UserUploadRecord) => {
    return colInfo.map((field, idx) => {
      return this.renderField(field, row, idx);
    });
  };

  renderField = (field: CSVField, data: UserUploadRecord, index: number) => {
    let content = '';
    switch (field.name) {
      // TODO: Use userUploadFields
      case 'assessment_1':
        content = data.quizTitlesEn[0];
        break;
      case 'assessment_2':
        content = data.quizTitlesEn[1];
        break;
      case 'assessment_3':
        content = data.quizTitlesEn[2];
        break;
      case 'group_1':
        content = data.userGroupNames[0];
        break;
      case 'group_2':
        content = data.userGroupNames[1];
        break;
      case 'group_3':
        content = data.userGroupNames[2];
        break;
      case 'group_4':
        content = data.userGroupNames[3];
        break;
      case 'group_5':
        content = data.userGroupNames[4];
        break;
      default:
        content = data[field.name];
    }
    return (
      <TableCell key={index}>
        <span className={fonts.caption}>{content}</span>
      </TableCell>
    );
  };

  renderAcceptFileForm = observer((client: WithDbId<AppClient>) => {
    const fileContent =
      this.fileData.contents.length > 0
        ? this.renderFileContent(this.getUploadDataFromContent(this.fileData.contents[0]))
        : null;
    return (
      <React.Fragment>
        <div className={styles.page}>
          <div className={styles.body}>
            <div className={styles.row}>
              {/* TODO aria (or DOM) labels */}
              <h3>{`${client.value.name} Groups and Users Bulk Upload`}</h3>
            </div>
            <div className={styles.row}>{this.renderTopButtons()}</div>
            <div className={styles.row}>
              <this.FileDropObserver />
            </div>
            <div className={styles.row}>
              <h4>
                {this.fileData.files.length > 0 &&
                  `Uploaded file: ${this.fileData.files.map(file => file.name).join('\n')}`}
              </h4>
            </div>
            <div className={styles.row}>{fileContent}</div>
          </div>
        </div>
      </React.Fragment>
    );
  });

  AcceptFileLoader = () => {
    const { client } = this.props;
    return <Loader state={client} LoadedComponent={this.renderAcceptFileForm} />;
  };

  render() {
    return (
      <this.props.pageLayout title={'Administration - Bulk User Upload'}>
        <this.AcceptFileLoader />
      </this.props.pageLayout>
    );
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/screens/appuser-upload-screen/appuser-upload-screen.tsx